import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Briefcase, Building2, Hotel, Landmark, LogOut, Radar, ShoppingBag, Stethoscope } from "lucide-react";
import { AppCanvas } from "@/components/glass/AppCanvas";
import { clearSession, getTenantId, setTenantId, getOperator } from "@/lib/auth";
import { applyTenantAttr } from "@/lib/theme";
import {
  firstAccessibleRoute,
  NO_ACCESS_ROUTE,
} from "@/lib/firstAccessibleRoute";
import { api, logout } from "@/lib/api";
import orikinWordmark from "@/assets/brand/orikin-wordmark-on-dark.png";
import "@/pages/login.css";

/**
 * §v3.profile-picker (2026-05-15) — post-login profile selector.
 *
 * One tile per workspace /api/admin/workspaces returns for this
 * operator; each tile's icon + industry caption come from the
 * workspace's `vertical` (its resolved industry pack), never from
 * its tenant id.
 *
 * The selector writes the chosen tenant_id to localStorage. Every
 * subsequent /api/admin/* call carries it via the X-Tenant-ID header
 * (lib/api.ts). The backend (tenant_context.get_current_tenant_id)
 * trusts the header and scopes Postgres reads + writes to that
 * tenant, so picking one workspace never touches another's data.
 *
 * Reached automatically after a successful login (LoginPage now
 * navigates here instead of straight to firstAccessibleRoute), and
 * via the "Switch profile" entry in the sidebar profile menu.
 *
 * §master-privacy (2026-06-12) — master accounts see ONLY the Mission
 * Control entry, never the tenant workspace tiles. Two reasons:
 *   1. Honesty: a Master Admin role carries only master.* permissions,
 *      so every tenant route's Guard bounces it back through
 *      firstAccessibleRoute → /master/tenants → /mission-control. The
 *      tiles were dead doors that silently looped into MC.
 *   2. Policy: platform admins have NO standing access to customer
 *      workspaces (least-privilege / plane separation — the founder's
 *      explicit call). If founder access to a tenant view is ever
 *      needed, build the audited "view as operator" impersonation flow
 *      in Mission Control instead of granting standing permissions.
 */

interface Profile {
  id: string;
  /** Tenant slug — written to localStorage on select. */
  tenant: string;
  name: string;
  tagline: string;
  Icon: typeof Briefcase;
  /** OKLCH accent for the tile — overrides the magenta default. */
  accent: string;
  description: string;
}

// §dashboard-first-run (2026-08-09) — the two hand-authored fallback cards
// are GONE. They were the picker's initial state and its
// fetch-error fallback, so every fresh tenant's owner saw a Roze Skincare
// card flash — and on an endpoint error saw it permanently, one click away
// from writing a FOREIGN tenant id into localStorage. "roze is NOT the
// default tenant": the picker now renders only what /api/admin/workspaces
// returns for THIS operator — skeleton while loading, retry on error.
const VERTICAL_STYLE: Record<string, { Icon: typeof Briefcase; accent: string; industry: string }> = {
  retail: { Icon: ShoppingBag, accent: "oklch(72% 0.18 320)", industry: "Retail & E-Commerce" },
  hospitality: { Icon: Hotel, accent: "oklch(72% 0.14 80)", industry: "Travel & Hospitality" },
  clinic: { Icon: Stethoscope, accent: "oklch(72% 0.14 160)", industry: "Healthcare" },
  real_estate: { Icon: Building2, accent: "oklch(72% 0.12 220)", industry: "Real Estate & Property" },
  financial_services: { Icon: Landmark, accent: "oklch(72% 0.1 260)", industry: "Financial Services" },
};

interface WorkspaceRow {
  tenant_id: string;
  brand_name: string;
  vertical: string | null;
}

function profileForWorkspace(w: WorkspaceRow): Profile {
  const style = VERTICAL_STYLE[w.vertical ?? ""] ?? {
    Icon: Briefcase,
    accent: "oklch(72% 0.18 320)",
    industry: "Business",
  };
  const name = w.brand_name || w.tenant_id;
  return {
    id: w.tenant_id,
    tenant: w.tenant_id,
    name,
    tagline: `${style.industry} · ORIKIN agent`,
    Icon: style.Icon,
    accent: style.accent,
    description: `Open the ${name} workspace — its own data, its own agent, fully isolated from every other tenant.`,
  };
}


export function ProfilePickerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [picking, setPicking] = useState<string | null>(null);
  // §dashboard-first-run (2026-08-09) — null = still loading (skeleton
  // tiles). The list is ONLY ever what the endpoint returned for this
  // operator; a fetch failure renders a retry card, never another tenant's.
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setProfiles(null);
    setLoadError(null);
    api<{ workspaces: WorkspaceRow[] }>("/api/admin/workspaces")
      .then((r) => {
        if (cancelled) return;
        setProfiles((r.workspaces ?? []).map(profileForWorkspace));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Couldn't load your workspaces.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);
  const [error, setError] = useState<string | null>(null);
  const operator = getOperator();
  const currentTenant = getTenantId();
  // §mission-control — the platform-admin account (master tenant / Master Admin
  // role) also gets the fleet operator console, which isn't a tenant workspace.
  const isMaster = operator?.tenant_id === "master" || operator?.role_name === "Master Admin";

  const goMissionControl = async () => {
    if (picking) return;
    setPicking("__mc__");
    setError(null);
    try {
      // Pin the master tenant so usePermissions() resolves master.tenants.view
      // and the /api/master/* calls authorize.
      setTenantId("master");
      applyTenantAttr();
      queryClient.clear();
      navigate("/mission-control", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open Mission Control");
      setPicking(null);
    }
  };

  // Decorative orbs identical to the LoginPage backdrop; we use the
  // login-variant AppCanvas (LiquidMetal shader) so the post-login
  // moment feels continuous with the sign-in moment.
  useEffect(() => {
    // No-op effect block kept for future hooks (e.g. analytics
    // "profile_picker_viewed" event).
  }, []);

  const handlePick = async (profile: Profile) => {
    if (picking) return;
    setPicking(profile.id);
    setError(null);
    try {
      // §v3.profile-picker — switching tenants drops every cached
      // React Query, so the next route's loaders refetch against the
      // new tenant scope.
      //
      // §v4.tenant-isolation (2026-08-13) — this used to invalidate
      // rather than clear. Invalidate only MARKS entries stale: query
      // keys carry no tenant, so stale-while-revalidate would paint
      // the previous workspace's customer rows for the first frame
      // after a switch. One tenant hid it; two did not.
      setTenantId(profile.tenant);
      // §v3.tenant-attr — re-stamp data-tenant on <html> so the
      // tenant-scoped CSS blocks activate / deactivate without a page
      // reload. The next render finds the attribute already set.
      applyTenantAttr();
      queryClient.clear();

      // Fetch the operator's permissions for the new tenant to
      // pick a sensible landing route.
      let permissions: string[] = [];
      try {
        const me = await api<{ permissions?: string[] }>("/api/admin/me");
        permissions = me.permissions ?? [];
      } catch {
        // Tenant scope might not have a /me record yet (first-time
        // pick into a fresh tenant). Fall back to a default route;
        // per-route Guards will redirect to /no-access if needed.
      }
      // Every workspace lands on the first route this operator's permissions
      // actually allow — no tenant-id special cases.
      const dest =
        permissions.length > 0
          ? firstAccessibleRoute(permissions)
          : NO_ACCESS_ROUTE;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't switch profile");
      setPicking(null);
    }
  };

  return (
    <div className="lp-stage">
      <AppCanvas variant="login" />

      <div
        className="lp-card"
        style={{ maxWidth: 720, padding: "44px 40px 36px" }}
      >
        <header className="lp-head" style={{ marginBottom: 28 }}>
          <img
            src={orikinWordmark}
            alt="ORIKIN AI"
            className="lp-wordmark"
            draggable={false}
          />
          <div className="lp-tagline">AGENTS</div>
        </header>

        <div
          style={{
            textAlign: "center",
            marginBottom: 22,
            color: "var(--fg-2)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {operator ? (
            <>
              Welcome back,{" "}
              <strong style={{ color: "var(--fg-1)" }}>
                {operator.display_name ?? operator.username}
              </strong>{" "}
              {isMaster
                ? "— you're signed in with the platform master account."
                : "— pick the workspace you're working in today."}
            </>
          ) : (
            "Pick the workspace you're working in today."
          )}
        </div>

        {/* Profile tile grid. Two tiles side-by-side on tablet+,
            stacked on mobile. §master-privacy — hidden for master
            accounts: they hold no tenant permissions, so the tiles were
            dead doors (every click bounced into Mission Control), and
            platform admins deliberately have no standing access to
            customer workspaces.
            §dashboard-first-run — loading → skeleton tiles; fetch error →
            retry card; empty list → say so. Never a hand-authored fallback
            card for a tenant this operator may not even belong to. */}
        {isMaster ? null : loadError ? (
          <div
            role="alert"
            style={{
              background: "var(--glass-2)",
              border: "1px solid var(--glass-hairline)",
              borderRadius: 16,
              padding: "24px 20px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>
              Couldn't load your workspaces
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: "var(--fg-3)",
              }}
            >
              {loadError}
            </div>
            <button
              type="button"
              onClick={() => setLoadAttempt((n) => n + 1)}
              style={{
                marginTop: 14,
                padding: "8px 18px",
                borderRadius: 999,
                background: "var(--glass-3)",
                border: "1px solid var(--glass-hairline-strong)",
                color: "var(--fg-1)",
                font: "inherit",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        ) : profiles == null ? (
          <div
            aria-hidden
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            }}
          >
            {[0, 1].map((i) => (
              <div
                key={i}
                style={{
                  background: "var(--glass-2)",
                  border: "1px solid var(--glass-hairline)",
                  borderRadius: 16,
                  padding: "20px 18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  animation: "lp-pulse 1.8s ease-in-out infinite",
                  animationDelay: `${i * 0.25}s`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: "var(--glass-3)",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      height: 12,
                      width: "55%",
                      borderRadius: 6,
                      background: "var(--glass-3)",
                    }}
                  />
                </div>
                <span
                  style={{
                    height: 10,
                    width: "85%",
                    borderRadius: 5,
                    background: "var(--glass-3)",
                  }}
                />
                <span
                  style={{
                    height: 10,
                    width: "60%",
                    borderRadius: 5,
                    background: "var(--glass-3)",
                  }}
                />
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div
            style={{
              background: "var(--glass-2)",
              border: "1px solid var(--glass-hairline)",
              borderRadius: 16,
              padding: "24px 20px",
              textAlign: "center",
              fontSize: 12.5,
              lineHeight: 1.55,
              color: "var(--fg-3)",
            }}
          >
            No workspaces are assigned to this account yet. Ask your
            administrator to grant access, then sign in again.
          </div>
        ) : (
        <div
          style={{
            display: "grid",
            gap: 14,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          {profiles.map((p) => {
            const isCurrent = p.tenant === currentTenant;
            const isPicking = picking === p.id;
            return (
              <button
                key={p.id}
                type="button"
                disabled={!!picking}
                onClick={() => void handlePick(p)}
                className="pp-tile"
                style={{
                  position: "relative",
                  background: "var(--glass-2)",
                  border: "1px solid var(--glass-hairline)",
                  borderRadius: 16,
                  padding: "20px 18px",
                  textAlign: "left",
                  color: "var(--fg-1)",
                  cursor: picking ? "wait" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  transition:
                    "transform 0.12s ease, border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
                  boxShadow: isCurrent
                    ? `0 0 0 1px ${p.accent} inset, 0 8px 24px -10px ${p.accent}`
                    : undefined,
                  opacity: picking && !isPicking ? 0.55 : 1,
                }}
                onMouseEnter={(e) => {
                  if (picking) return;
                  e.currentTarget.style.background = "var(--glass-3)";
                  e.currentTarget.style.borderColor = "var(--glass-hairline-strong)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  if (picking) return;
                  e.currentTarget.style.background = "var(--glass-2)";
                  e.currentTarget.style.borderColor = "var(--glass-hairline)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 12,
                      background: `color-mix(in oklab, ${p.accent} 18%, var(--glass-3))`,
                      border: `1px solid color-mix(in oklab, ${p.accent} 42%, var(--glass-hairline))`,
                      display: "grid",
                      placeItems: "center",
                      color: p.accent,
                      flexShrink: 0,
                    }}
                    aria-hidden
                  >
                    <p.Icon size={20} strokeWidth={1.75} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "-0.005em",
                        color: "var(--fg-1)",
                      }}
                    >
                      {p.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--fg-3)",
                        letterSpacing: "0.02em",
                        marginTop: 2,
                      }}
                    >
                      {p.tagline}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: "var(--fg-2)",
                    lineHeight: 1.5,
                  }}
                >
                  {p.description}
                </div>
                {/* Bottom row: status chip or spinner */}
                <div
                  style={{
                    marginTop: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {isCurrent ? (
                    <span
                      style={{
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: p.accent,
                        fontWeight: 600,
                      }}
                    >
                      Active
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 10.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "var(--fg-3)",
                      }}
                    >
                      Tenant · {p.tenant}
                    </span>
                  )}
                  {isPicking ? (
                    <span className="lp-submit-spinner" aria-hidden />
                  ) : (
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--fg-3)",
                      }}
                    >
                      Continue →
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        )}

        {isMaster ? (
          <button
            type="button"
            disabled={!!picking}
            onClick={() => void goMissionControl()}
            className="pp-tile"
            style={{
              marginTop: 14,
              width: "100%",
              background: "color-mix(in oklab, oklch(64% 0.13 264) 12%, var(--glass-2))",
              border: "1px solid color-mix(in oklab, oklch(64% 0.13 264) 40%, var(--glass-hairline))",
              borderRadius: 16,
              padding: "16px 18px",
              textAlign: "left",
              color: "var(--fg-1)",
              cursor: picking ? "wait" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 12,
              opacity: picking && picking !== "__mc__" ? 0.55 : 1,
            }}
          >
            <span
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "color-mix(in oklab, oklch(64% 0.13 264) 18%, var(--glass-3))",
                border: "1px solid color-mix(in oklab, oklch(64% 0.13 264) 42%, var(--glass-hairline))",
                display: "grid",
                placeItems: "center",
                color: "oklch(74% 0.13 264)",
                flexShrink: 0,
              }}
              aria-hidden
            >
              <Radar size={20} strokeWidth={1.75} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--fg-1)" }}>Mission Control</div>
              <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>
                Fleet operator console · all tenants
              </div>
            </div>
            {picking === "__mc__" ? (
              <span className="lp-submit-spinner" aria-hidden />
            ) : (
              <span style={{ fontSize: 10.5, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Platform →
              </span>
            )}
          </button>
        ) : null}

        {/* §master-privacy — say WHY there are no workspace tiles, so the
            single-option screen reads as a policy, not a bug. */}
        {isMaster ? (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              lineHeight: 1.55,
              color: "var(--fg-3)",
              textAlign: "center",
            }}
          >
            Master accounts manage the platform from Mission Control and have no
            access to customer workspaces — each tenant's data stays private to
            its own operators.
          </div>
        ) : null}

        {error ? (
          <div role="alert" className="lp-error" style={{ marginTop: 18 }}>
            {error}
          </div>
        ) : null}

        <footer className="lp-foot" style={{ marginTop: 24 }}>
          <div className="lp-status">
            <span className="lp-status-dot" aria-hidden />
            All systems operational
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {/* §mc-exit-fix — the picker is the ONLY authed surface outside
                Mission Control for a master operator (no AppShell access),
                so it must carry its own sign-out or the master account can
                never log out. Same flow as AppShell's handleLogout. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  await logout();
                } finally {
                  clearSession();
                  queryClient.clear();
                  navigate("/login", { replace: true });
                }
              }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 12px", borderRadius: 999,
                background: "transparent",
                border: "1px solid var(--glass-hairline, rgba(255,255,255,0.10))",
                color: "var(--fg-2, rgba(255,255,255,0.76))",
                font: "inherit", fontSize: 12.5, cursor: "pointer",
              }}
            >
              <LogOut size={13} aria-hidden /> Sign out
            </button>
            <div className="lp-meta">ORIKIN AI · admin</div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default ProfilePickerPage;
